import { inventoryTrackingMode } from './inventoryUnitModel.js';

function qty(value, fallback = 0) {
  const parsed = Number(String(value ?? '').trim());
  return Number.isFinite(parsed) ? parsed : fallback;
}

function round(value) {
  return Math.round((Number(value) || 0) * 1000) / 1000;
}

function appendNote(existing, text) {
  const base = String(existing || '').trim();
  return base ? `${base} | ${text}` : text;
}

function safeId(value) {
  return String(value || '').replace(/[^A-Za-z0-9_.:-]+/g, '-');
}

export function groupRestockItems(items) {
  const grouped = new Map();
  (Array.isArray(items) ? items : []).forEach(item => {
    const assetId = String(item?.assetId || '').trim().toUpperCase();
    const catalogId = String(item?.catalogId || '').trim();
    if (!assetId || !catalogId) return;
    const key = `${assetId}|${catalogId}`;
    const current = grouped.get(key);
    if (current) {
      current.reportedUsed = round(current.reportedUsed + qty(item?.quantity));
      return;
    }
    grouped.set(key, {
      key,
      assetId,
      assetName: String(item?.assetName || assetId).trim() || assetId,
      catalogId,
      component: String(item?.component || '').trim(),
      item: String(item?.item || catalogId).trim() || catalogId,
      reportedUsed: Math.max(0, qty(item?.quantity)),
    });
  });
  return [...grouped.values()];
}

export function buildRestockReconciliationDraft(request, inventory) {
  const catalog = Array.isArray(inventory?.catalog) ? inventory.catalog : [];
  const expirations = Array.isArray(inventory?.expirations) ? inventory.expirations : [];
  const catalogById = new Map(catalog.map(item => [String(item?.catalogId || '').trim(), item]));

  return groupRestockItems(request?.items).map(item => {
    const standard = catalogById.get(item.catalogId);
    const trackingMode = inventoryTrackingMode(standard, expirations);
    const oldLots = expirations
      .filter(record =>
        String(record?.setId || '').trim().toUpperCase() === item.assetId &&
        String(record?.catalogId || '').trim() === item.catalogId &&
        !record?.returnedToLogistics &&
        qty(record?.requiredQty) > 0
      )
      .map(record => ({
        recordId: String(record.recordId || '').trim(),
        beforeQty: Math.max(0, qty(record.requiredQty)),
        remainingQty: Math.max(0, qty(record.requiredQty)),
        expirationDate: String(record.expirationDate || '').trim(),
        expirationState: String(record.expirationState || 'UNKNOWN').trim().toUpperCase(),
        lotSerial: String(record.lotSerial || '').trim(),
        notes: String(record.notes || '').trim(),
        sourceRecord: record,
      }));

    if (oldLots.length === 1) {
      oldLots[0].remainingQty = Math.max(0, round(oldLots[0].beforeQty - item.reportedUsed));
    }

    return {
      ...item,
      trackingMode,
      standardQty: Math.max(0, qty(standard?.requiredQty)),
      oldLots,
      replacements: [],
      untrackedUse: oldLots.length === 0 ? item.reportedUsed : 0,
    };
  });
}

export function addReplacementLine(group, seed = {}) {
  const stamp = String(seed.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const counted = group?.trackingMode === 'counted';
  return {
    id: stamp,
    recordId: safeId(seed.recordId || `${group.assetId}-${group.catalogId}-RESTOCK-${counted ? 'COUNT' : 'UNIT'}-${stamp}`),
    barcode: String(seed.barcode || '').trim(),
    quantity: counted ? String(seed.quantity ?? '1') : '1',
    expirationDate: counted ? '' : String(seed.expirationDate || '').trim(),
    noExpiration: counted ? true : Boolean(seed.noExpiration),
    lotSerial: counted ? '' : String(seed.lotSerial || '').trim(),
  };
}

export function summarizeReconciliationGroup(group) {
  const recordedBefore = round((group.oldLots || []).reduce((sum, lot) => sum + Math.max(0, qty(lot.beforeQty)), 0));
  const remaining = round((group.oldLots || []).reduce((sum, lot) => sum + Math.max(0, qty(lot.remainingQty)), 0));
  const inferredConsumed = round(Math.max(0, recordedBefore - remaining));
  const replacementQty = round((group.replacements || []).reduce((sum, line) => sum + Math.max(0, qty(line.quantity)), 0));
  const finalQty = round(remaining + replacementQty);
  const reportedUsed = round(Math.max(0, qty(group.reportedUsed)));
  const standardQty = round(Math.max(0, qty(group.standardQty)));
  const accountedConsumed = round(inferredConsumed + Math.max(0, qty(group.untrackedUse)));
  return { recordedBefore, remaining, inferredConsumed, replacementQty, finalQty, reportedUsed, standardQty, accountedConsumed };
}

export function validateReconciliationGroup(group) {
  const errors = [];
  const summary = summarizeReconciliationGroup(group);
  const counted = group?.trackingMode === 'counted';

  (group.oldLots || []).forEach(lot => {
    const remaining = qty(lot.remainingQty, -1);
    const before = qty(lot.beforeQty);
    if (remaining < 0 || remaining > before) {
      errors.push(`${group.item}: remaining quantity for lot ${lot.lotSerial || lot.recordId} must be between 0 and ${lot.beforeQty}.`);
    }
    if (!counted && before === 1 && ![0, 1].includes(remaining)) {
      errors.push(`${group.item}: an individually tracked unit must be either still present (1) or consumed (0).`);
    }
  });

  if (Math.abs(summary.accountedConsumed - summary.reportedUsed) > 0.001) {
    errors.push(`${group.item}: the confirmed remaining inventory accounts for ${summary.accountedConsumed} used, but the return reported ${summary.reportedUsed}.`);
  }

  (group.replacements || []).forEach((line, index) => {
    if (counted) {
      if (!Number.isInteger(qty(line.quantity)) || qty(line.quantity) <= 0) errors.push(`${group.item}: replacement ${index + 1} needs a whole-number counted quantity greater than zero.`);
    } else {
      if (qty(line.quantity) !== 1) errors.push(`${group.item}: replacement ${index + 1} must be saved as one physical unit.`);
      if (!line.noExpiration && !String(line.expirationDate || '').trim()) errors.push(`${group.item}: replacement ${index + 1} needs an expiration date or N/A.`);
    }
  });

  if (summary.standardQty > 0 && summary.finalQty < summary.standardQty) {
    errors.push(`${group.item}: final quantity ${summary.finalQty} is below the standard of ${summary.standardQty}.`);
  }

  return { valid: errors.length === 0, errors, summary };
}

export function validateRestockReconciliation(groups) {
  const checks = (Array.isArray(groups) ? groups : []).map(validateReconciliationGroup);
  const errors = checks.flatMap(check => check.errors);
  return { valid: groups?.length > 0 && errors.length === 0, errors, checks };
}

export function buildRestockInventoryUpdates(groups, requestCode, now = new Date()) {
  const dateText = now.toISOString().slice(0, 10);
  const updates = [];

  (Array.isArray(groups) ? groups : []).forEach(group => {
    const check = validateReconciliationGroup(group);
    if (!check.valid) throw new Error(check.errors[0]);
    const counted = group?.trackingMode === 'counted';

    if (counted) {
      (group.oldLots || []).forEach(lot => {
        const consumed = round(Math.max(0, qty(lot.beforeQty) - qty(lot.remainingQty)));
        updates.push({
          ...lot.sourceRecord,
          requiredQty: '0',
          expirationDate: '',
          expirationState: 'NA',
          notes: appendNote(lot.notes, consumed > 0 ? `Consumed ${consumed} during ${requestCode}; superseded by final counted inventory` : `Reconciled during ${requestCode}; superseded by final counted inventory`),
        });
      });

      const replacementBarcode = (group.replacements || []).map(line => line.barcode).filter(Boolean).join(', ');
      updates.push({
        recordId: safeId(`${group.assetId}-${group.catalogId}-RESTOCK-COUNT-${requestCode}-${dateText}`),
        setId: group.assetId,
        setName: group.assetName,
        catalogId: group.catalogId,
        component: group.component,
        item: group.item,
        requiredQty: String(check.summary.finalQty),
        expirationDate: '',
        expirationState: 'NA',
        lotSerial: '',
        returnedToLogistics: false,
        returnedDate: '',
        replacementDate: dateText,
        notes: appendNote('Counted / bulk inventory snapshot', `Reconciled during ${requestCode}${replacementBarcode ? `; barcode ${replacementBarcode}` : ''}`),
        updatedAt: '',
      });
      return;
    }

    (group.oldLots || []).forEach(lot => {
      const remaining = round(Math.max(0, qty(lot.remainingQty)));
      const consumed = round(Math.max(0, qty(lot.beforeQty) - remaining));
      const originalExpiration = String(lot.expirationDate || '').trim();
      const auditText = consumed > 0
        ? `Consumed ${consumed} during ${requestCode}${originalExpiration ? `; original expiration ${originalExpiration}` : ''}`
        : `Reconciled during ${requestCode}`;
      updates.push({
        ...lot.sourceRecord,
        requiredQty: String(remaining),
        expirationDate: remaining === 0 ? '' : originalExpiration,
        expirationState: remaining === 0 ? 'NA' : (lot.expirationState || (originalExpiration ? 'DATED' : 'UNKNOWN')),
        notes: appendNote(lot.notes, auditText),
      });
    });

    (group.replacements || []).forEach(line => {
      updates.push({
        recordId: line.recordId,
        setId: group.assetId,
        setName: group.assetName,
        catalogId: group.catalogId,
        component: group.component,
        item: group.item,
        requiredQty: '1',
        expirationDate: line.noExpiration ? '' : String(line.expirationDate || '').trim(),
        expirationState: line.noExpiration ? 'NA' : 'DATED',
        lotSerial: String(line.lotSerial || '').trim(),
        returnedToLogistics: false,
        returnedDate: '',
        replacementDate: dateText,
        notes: appendNote('Individual physical unit', `Added during ${requestCode}${line.barcode ? `; barcode ${line.barcode}` : ''}`),
        updatedAt: '',
      });
    });
  });

  return updates;
}

export function buildRestockReconciliationAudit(groups) {
  return (Array.isArray(groups) ? groups : []).map(group => {
    const summary = summarizeReconciliationGroup(group);
    return {
      assetId: group.assetId,
      assetName: group.assetName,
      catalogId: group.catalogId,
      component: group.component,
      item: group.item,
      trackingMode: group.trackingMode || 'unit',
      reportedUsed: summary.reportedUsed,
      standardQty: summary.standardQty,
      recordedBefore: summary.recordedBefore,
      confirmedRemaining: summary.remaining,
      inferredConsumed: summary.inferredConsumed,
      untrackedConsumed: round(Math.max(0, qty(group.untrackedUse))),
      replacementQty: summary.replacementQty,
      finalQty: summary.finalQty,
      oldLots: (group.oldLots || []).map(lot => ({
        recordId: lot.recordId,
        lotSerial: lot.lotSerial,
        expirationDate: lot.expirationDate,
        beforeQty: round(qty(lot.beforeQty)),
        remainingQty: round(qty(lot.remainingQty)),
        consumedQty: round(Math.max(0, qty(lot.beforeQty) - qty(lot.remainingQty))),
      })),
      replacements: (group.replacements || []).map(line => ({
        recordId: line.recordId,
        barcode: line.barcode,
        quantity: round(qty(line.quantity)),
        expirationDate: line.noExpiration ? '' : line.expirationDate,
        expirationState: line.noExpiration ? 'NA' : 'DATED',
        lotSerial: line.lotSerial,
      })),
    };
  });
}
